import React, { useState } from "react";
import theme from "../../../theme";
import uploadIcon from "../../../assets/icons/upload.svg";
import errorIcon from "../../../assets/icons/errorIcon.svg";

import Swal from "sweetalert2";
import {
  Container,
  FormWrapper,
  SliderWrapper,
  RadioGroup,
  CheckboxGroup,
  ProofOfPaymentContainer,
  FileInputLabel,
  FileInput,
  PreviewImage,
  InputWrapper,
  Tooltip,
} from "./CreateCampaignTabStyles";
import { Slider, FormControlLabel, Radio, Checkbox } from "@mui/material";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { createCampaign } from "../../../redux/slices/campaignSlice";

import { countries, objectives } from "../../../shared/data/campaigns";

const CreateCampaignTab = () => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleFocus = () => setShowTooltip(true);
  const handleBlur = () => {
    setShowTooltip(false);
    if (
      !formData.campaignUrl.startsWith("http://") &&
      !formData.campaignUrl.startsWith("https://")
    ) {
      Swal.fire({
        title: "Invalid URL",
        html: "The URL must start with <strong>'http://'</strong> or <strong>'https://'</strong>. Please correct it.",
        icon: null,
        iconHtml: `<img src=${errorIcon} alt="error" style="width:50px;">`,
        confirmButtonText: "Okay",
      });
    }
  };

  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.campaign);
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    campaignName: "",
    campaignUrl: "",
    campaignBudget: "",
    campaignObjective: "",
    ageRange: [25, 50],
    gender: "both",
    interests: {
      entertainment: false,
      lifestyle: false,
      gaming: false,
      music: false,
      others: false,
    },
    customInterests: [],
    geo: "",
    startDate: "",
    endDate: "",
    campaignPhoto: null,
  });

  const [customInterestInput, setCustomInterestInput] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAgeChange = (event, newValue) => {
    setFormData({
      ...formData,
      ageRange: newValue,
    });
  };

  const handleGenderChange = (event) => {
    setFormData({
      ...formData,
      gender: event.target.value,
    });
  };

  // const handleInterestChange = (event) => {
  //   setFormData({
  //     ...formData,
  //     interests: {
  //       ...formData.interests,
  //       [event.target.name]: event.target.checked,
  //     },
  //   });
  // };

  const handleInterestChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      interests: {
        ...prevState.interests,
        [name]: checked,
      },
      // If unchecking "others", also clear any custom interests
      customInterests:
        name === "others" && !checked ? [] : prevState.customInterests,
    }));
  };

  const handleSelectChange = (selectedOption, name) => {
    setFormData({
      ...formData,
      [name]: selectedOption.value,
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      campaignPhoto: e.target.files[0],
    });
  };

  const handleCustomInterestKeyDown = (event) => {
    if (event.key === "Enter" && customInterestInput.trim()) {
      event.preventDefault();
      // Avoid duplicates
      if (!formData.customInterests.includes(customInterestInput.trim())) {
        setFormData((prevState) => ({
          ...prevState,
          customInterests: [
            ...prevState.customInterests,
            customInterestInput.trim(),
          ],
        }));
      }
      setCustomInterestInput("");
    }
  };

  const removeCustomInterest = (interest) => {
    setFormData((prevState) => ({
      ...prevState,
      customInterests: prevState.customInterests.filter((i) => i !== interest),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !formData.campaignUrl.startsWith("http://") &&
      !formData.campaignUrl.startsWith("https://")
    ) {
      Swal.fire({
        title: "Invalid URL",
        html: "The URL must start with <strong>'http://'</strong> or <strong>'https://'</strong>. Please correct it.",
        icon: null,
        iconHtml: `<img src=${errorIcon} alt="error" style="width:50px;">`,
        confirmButtonText: "Okay",
      });
      return;
    }

    const form = new FormData();
    form.append("campaignName", formData.campaignName);
    form.append("campaignUrl", formData.campaignUrl);
    form.append("flow", formData.flow);
    form.append("campaignObjective", formData.campaignObjective);
    form.append("campaignBudget", formData.campaignBudget);
    form.append("ageRange", `${formData.ageRange[0]}-${formData.ageRange[1]}`);
    form.append("gender", formData.gender);
    form.append("location", formData.geo);
    form.append("carrier", formData.carrier);
    form.append("startDate", formData.startDate);
    form.append("endDate", formData.endDate);

    const selectedInterests = Object.keys(formData.interests).filter(
      (interest) => formData.interests[interest]
    );

    if (
      selectedInterests.length === 0 &&
      formData.customInterests.length === 0
    ) {
      selectedInterests.push("others");
    }

    // Append each selected interest individually
    selectedInterests.forEach((interest) => {
      form.append("interest[]", interest);
    });

    if (formData.customInterests.length > 0) {
      formData.customInterests.forEach((interest) => {
        form.append("interest[]", interest);
      });
    }

    if (formData.campaignPhoto) {
      form.append("files", formData.campaignPhoto);
    }

    dispatch(createCampaign(form));
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      marginTop: "10px",
      border: state.isFocused ? `solid 1px ${theme.color.brand}` : "solid 1px #e0e2e6",
      borderRadius: "10px",
      padding: "0px 14px",
      transition: "all 0.3s ease",
      height: "43px",
      boxShadow: state.isFocused ? `0 0 0 0px ${theme.color.brand}` : "none",
      "&:hover": {
        borderColor: state.isFocused ? theme.color.brand : "",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.color.brand
        : state.isFocused
        ? "#e0f2f1"
        : provided.backgroundColor,
      color: state.isSelected ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#b2dfdb",
        color: "#000",
      },
    }),
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <FormWrapper>
          <label htmlFor="campaignName">Campaign Name</label>
          <input
            type="text"
            id="campaignName"
            name="campaignName"
            placeholder="Campaign Name"
            value={formData.campaignName}
            onChange={handleChange}
            required
          />
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="campaignUrl">Campaign Url</label>
          <InputWrapper>
            <input
              type="text"
              id="campaignUrl"
              name="campaignUrl"
              placeholder="Campaign Url"
              value={formData.campaignUrl}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />
            {showTooltip && (
              <Tooltip>
                <strong>Tracking Parameters</strong>
                <p>
                  use {"{clickID}"} to track click id, and {"{sourceID}"} to
                  track
                  <br />
                  sub source eg:{" "}
                  {"https://google.com?clickId={clickID}&sourceId={sourceID}"}
                </p>
              </Tooltip>
            )}
          </InputWrapper>
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="flow">Flow</label>
          <input
            type="text"
            id="flow"
            name="flow"
            placeholder="Flow"
            value={formData.flow}
            onChange={handleChange}
            required
          />
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="campaignBudget">Campaign Budget ($)</label>
          <input
            type="number"
            id="campaignBudget"
            name="campaignBudget"
            placeholder="Enter your campaign budget in dollars"
            value={formData.campaignBudget}
            onChange={handleChange}
            required
          />
        </FormWrapper>

        {/* <FormWrapper>
          <div>
            <label htmlFor="monthlyCampaignBudget">Monthly Campaign Budget ($)</label>
            <p>This is your budget for this month’s campaign</p>
          </div>
          <input
            type="number"
            id="monthlyCampaignBudget"
            name="monthlyCampaignBudget"
            placeholder="Enter your campaign budget in dollars"
            value={formData.monthlyCampaignBudget}
            onChange={handleChange}
            required
          />
        </FormWrapper> */}

        <FormWrapper>
          <label htmlFor="campaignObjective">Campaign objective</label>
          <Select
            options={objectives}
            styles={customStyles}
            placeholder="Select Objective"
            onChange={(option) =>
              handleSelectChange(option, "campaignObjective")
            }
            required
          />
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="ageRange">Age range</label>
          <SliderWrapper>
            <Slider
              sx={{
                color: "#33615A",
                height: "10px",
                "& .MuiSlider-thumb": {
                  backgroundColor: "#FFFFFF",
                  height: "30px",
                  width: "30px",
                },
              }}
              id="ageRange"
              name="ageRange"
              min={10}
              max={100}
              step={1}
              value={formData.ageRange}
              onChange={handleAgeChange}
              valueLabelDisplay="auto"
            />
          </SliderWrapper>
        </FormWrapper>

        <FormWrapper>
          <div>
            <label>Preferred gender</label>
            <p>Choose the gender you will want your ads to be targeted at</p>
          </div>
          <RadioGroup>
            <FormControlLabel
              control={
                <Radio
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Male"
              value="male"
              checked={formData.gender === "male"}
              onChange={handleGenderChange}
            />
            <FormControlLabel
              control={
                <Radio
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Female"
              value="female"
              checked={formData.gender === "female"}
              onChange={handleGenderChange}
            />
            <FormControlLabel
              control={
                <Radio
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Both"
              value="both"
              checked={formData.gender === "both"}
              onChange={handleGenderChange}
            />
          </RadioGroup>
        </FormWrapper>

        
        <FormWrapper>
          <div>
            <label>Interest</label>
            <p>Choose the kind of user’s interest you will like to target</p>
          </div>
          <CheckboxGroup>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Entertainment"
              name="entertainment"
              checked={formData.interests.entertainment}
              onChange={handleInterestChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Lifestyle"
              name="lifestyle"
              checked={formData.interests.lifestyle}
              onChange={handleInterestChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Gaming"
              name="gaming"
              checked={formData.interests.gaming}
              onChange={handleInterestChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Music"
              name="music"
              checked={formData.interests.music}
              onChange={handleInterestChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#33615A",
                    },
                  }}
                />
              }
              label="Others"
              name="others"
              checked={formData.interests.others}
              onChange={handleInterestChange}
            />
          </CheckboxGroup>
          <p></p>
          {formData.interests.others && (
            <div style={{ marginTop: "-25px" }}>
              <input
                type="text"
                placeholder="Enter new interest and press Enter"
                value={customInterestInput}
                onChange={(e) => setCustomInterestInput(e.target.value)}
                onKeyDown={handleCustomInterestKeyDown}
                style={{
                  padding: "8px",
                  width: "100%",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                }}
              />
              <div style={{ marginTop: "10px" }}>
                {formData.customInterests.map((interest, index) => (
                  <span
                    key={index}
                    style={{
                      display: "inline-block",
                      background: "#e0f2f1",
                      padding: "5px 10px",
                      borderRadius: "15px",
                      marginRight: "5px",
                      marginBottom: "5px",
                    }}
                  >
                    {interest}
                    <button
                      onClick={() => removeCustomInterest(interest)}
                      style={{
                        marginLeft: "5px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                      type="button"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="geo">Geo</label>
          <Select
            options={countries}
            styles={customStyles}
            placeholder="Select Country"
            onChange={(option) => handleSelectChange(option, "geo")}
            required
          />
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="carrier">Carrier</label>
          <input
            type="text"
            id="carrier"
            name="carrier"
            placeholder="Carrier"
            value={formData.carrier}
            onChange={handleChange}
            required
          />
        </FormWrapper>

        <FormWrapper>
          <div>
            <label htmlFor="campaignPhoto">Campaign photo</label>
            <p>A screenshot of your campaign landing page</p>
          </div>
          <ProofOfPaymentContainer>
            <FileInputLabel htmlFor="campaignPhoto">
              {formData.campaignPhoto ? (
                <PreviewImage
                  src={URL.createObjectURL(formData.campaignPhoto)}
                  alt="Campaign Photo"
                />
              ) : (
                <div className="text">
                  <img src={uploadIcon} alt="" />
                  <p>Click to upload</p>
                  {/* <small>PNG, JPG</small> */}
                </div>
              )}
              <FileInput
                type="file"
                id="campaignPhoto"
                name="campaignPhoto"
                accept=".svg,.png,.jpg"
                onChange={handleFileChange}
              />
            </FileInputLabel>
          </ProofOfPaymentContainer>
        </FormWrapper>

        <FormWrapper>
          <label>Date</label>
          <div className="date">
            <div>
              <label htmlFor="startDate">Start date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                min={today}
                required
              />
            </div>
            <div>
              <label htmlFor="endDate">End date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={today}
                required
              />
            </div>
          </div>
        </FormWrapper>

        <div className="submit">
          <div></div>
          <div className="submit-buttons">
            <button className="add-campaign" type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </div>
      </form>
    </Container>
  );
};

export default CreateCampaignTab;
